/**
 * Inicio — dashboard de arranque del turno. Mezcla KPIs feature-aware
 * (solo aparecen los que el tenant tiene activos) con shortcuts a las
 * secciones más usadas. El layout es el mismo que desktop pero con las
 * features opcionales del tenant integradas sin saturar al que solo
 * tiene el core.
 */

import { Button, Card, CardContent, CardHeader, CardTitle } from '@atreus/ui';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  BarChart3,
  Coins,
  ConciergeBell,
  Package,
  Receipt,
  Tag,
  UtensilsCrossed,
} from 'lucide-react';
import { useMemo, type ComponentType, type ReactNode } from 'react';

import { loadSession } from '../auth/session.js';
import { useFeatures } from '../lib/features.js';
import { formatCOP } from '../lib/format.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';

export const HomePage = () => {
  const session = loadSession();
  const features = useFeatures();
  const permissions = usePermissions();

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-8">
      <header>
        <div className="text-sm uppercase tracking-[0.2em] text-fg-muted">
          Bienvenido{session?.user.fullName ? `, ${session.user.fullName.split(' ')[0]}` : ''}
        </div>
        <h1 className="mt-1 text-4xl font-black tracking-tight">
          {session?.tenantName ?? 'Atreus'}
        </h1>
        <p className="mt-2 max-w-xl text-fg-muted">
          Resumen del día y accesos rápidos.
        </p>
      </header>

      <LowStockBanner />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {permissions.canDo('products.view') ? <ProductsCard /> : null}
        {permissions.canDo('categories.view') ? <CategoriesCard /> : null}
        {permissions.canDo('orders.view') ? <OrdersCard /> : null}
        {features.isEnabled('tables') &&
        permissions.canDo('tables.view') ? (
          <TablesCard />
        ) : null}
        {permissions.canDo('cash.openOwn') ? <CashCard /> : null}
        {features.isEnabled('stats') &&
        permissions.canDo('stats.view') ? (
          <TodaySalesCard />
        ) : null}
      </div>
    </div>
  );
};

// ---------- Low stock banner ----------

const LowStockBanner = () => {
  const features = useFeatures();
  const permissions = usePermissions();
  const enabled =
    features.isEnabled('inventory') && permissions.canDo('inventory.view');
  // COUNT(*) server-side — el banner solo necesita saber cuántos hay, no
  // la lista. Evita traer el inventario completo para el dashboard.
  const query = trpc.inventory.count.useQuery(
    { lowStockOnly: true },
    {
      enabled,
      refetchInterval: 30_000,
    },
  );

  if (!enabled) return null;
  const alertCount = query.data?.total ?? 0;
  if (alertCount === 0) return null;

  return (
    <Card className="border-warning/40 bg-warning/5">
      <CardContent className="flex items-center gap-4 pt-4">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-warning/20 text-warning">
          <AlertTriangle className="size-5" />
        </span>
        <div className="flex-1">
          <div className="font-bold">
            {alertCount === 1
              ? '1 producto con bajo stock'
              : `${alertCount} productos con bajo stock`}
          </div>
          <p className="text-sm text-fg-muted">
            Revisa el inventario y reabastece lo que está por acabarse o ya
            se agotó.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/inventory">Ver inventario</Link>
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------- Cards ----------

const ProductsCard = () => {
  // Usamos `products.count` en vez de `list` — un COUNT(*) indexado en vez
  // de traer el dataset entero (puede ser miles de filas).
  const products = trpc.products.count.useQuery({ activeOnly: false });
  return (
    <StatCard
      icon={Package}
      title="Productos"
      value={products.data?.total ?? '—'}
      subtitle="Registrados"
      cta="Gestionar"
      to="/products"
    />
  );
};

const CategoriesCard = () => {
  const categories = trpc.categories.list.useQuery();
  return (
    <StatCard
      icon={Tag}
      title="Categorías"
      value={categories.data?.length ?? '—'}
      subtitle="Grupos del menú"
      cta="Gestionar"
      to="/categories"
    />
  );
};

const OrdersCard = () => {
  // Solo un COUNT(*) server-side — no trae filas.
  const query = trpc.orders.count.useQuery(
    { activeOnly: true },
    { refetchInterval: 15_000 },
  );
  return (
    <StatCard
      icon={UtensilsCrossed}
      title="Pedidos"
      value={query.isLoading ? '—' : (query.data?.total ?? 0)}
      subtitle="Activos en este momento"
      cta="Abrir pedidos"
      to="/orders"
    />
  );
};

const TablesCard = () => {
  // Solo necesitamos pedidos activos para saber qué mesas están ocupadas.
  // activeOnly filtra server-side; 500 es techo defensivo (un restobar rara
  // vez tiene más de ~50 pedidos activos simultáneos).
  const orders = trpc.orders.list.useQuery(
    { activeOnly: true, limit: 500 },
    { refetchInterval: 15_000 },
  );
  const tenant = trpc.tenant.current.useQuery();

  // Deriva total de mesas desde el configJson de la feature tables.
  const tableIds = new Set<string>();
  try {
    const raw = tenant.data?.features.find((f) => f.featureId === 'tables')
      ?.configJson;
    if (raw) {
      const cfg = JSON.parse(raw) as {
        zones?: { tables?: { id?: string; blocked?: boolean }[] }[];
      };
      for (const z of cfg.zones ?? []) {
        for (const t of z.tables ?? []) {
          if (t.id && !t.blocked) tableIds.add(t.id);
        }
      }
    }
  } catch {
    // configJson mal formado — mostramos 0.
  }

  const busyTableIds = new Set<string>();
  for (const o of orders.data?.items ?? []) {
    if (o.origin !== 'table') continue;
    if (o.tableId) busyTableIds.add(o.tableId);
  }

  const total = tableIds.size;
  const busy = busyTableIds.size;
  const display = total > 0 ? `${busy}/${total}` : '—';

  return (
    <StatCard
      icon={ConciergeBell}
      title="Mesas"
      value={display}
      subtitle={
        total === 0
          ? 'Sin plano configurado'
          : busy === 0
            ? 'Todas libres'
            : busy === total
              ? 'Salón lleno'
              : `${total - busy} libres`
      }
      cta="Ver salón"
      to="/tables"
    />
  );
};

const CashCard = () => {
  const session = trpc.cash.currentSession.useQuery(undefined, {
    refetchInterval: 10_000,
  });
  const summary = trpc.cash.summary.useQuery(undefined, {
    refetchInterval: 10_000,
  });
  const open = session.data != null;
  return (
    <StatCard
      icon={Coins}
      title="Caja"
      value={open ? formatCOP(summary.data?.expectedCashCents ?? 0) : '—'}
      subtitle={open ? 'Efectivo esperado en caja' : 'Sin sesión abierta'}
      cta={open ? 'Ver movimientos' : 'Abrir caja'}
      to="/cash"
    />
  );
};

/**
 * Card que muestra ventas de HOY — totales + # pedidos. Usa el mismo
 * rango que `/stats` con preset `today` pero acá lo calculamos inline
 * para no compartir dependencia.
 */
const TodaySalesCard = () => {
  const range = useMemo(() => {
    const now = Date.now();
    const BOGOTA_OFFSET_MS = -300 * 60_000;
    const DAY_MS = 86_400_000;
    const wall = now + BOGOTA_OFFSET_MS;
    const wallStartOfDay = Math.floor(wall / DAY_MS) * DAY_MS;
    const todayStartUtc = wallStartOfDay - BOGOTA_OFFSET_MS;
    return { from: todayStartUtc, to: now };
  }, []);

  const summary = trpc.stats.summary.useQuery(range, {
    refetchInterval: 30_000,
  });
  const d = summary.data;
  const hasData = d != null;
  const revenue = d?.revenueCents;
  const value = hasData
    ? revenue != null
      ? formatCOP(revenue)
      : `${d.orderCount} pedidos`
    : '—';
  const subtitle = hasData
    ? revenue != null
      ? `${d.orderCount} pedidos hoy · ticket prom. ${
          d.avgTicketCents != null ? formatCOP(d.avgTicketCents) : '—'
        }`
      : 'Ventas de hoy'
    : 'Ventas de hoy';

  return (
    <StatCard
      icon={BarChart3}
      title="Ventas hoy"
      value={value}
      subtitle={subtitle}
      cta="Ver dashboard"
      to="/stats"
      badge={
        hasData && d.orderCount > 0 ? (
          <span className="flex items-center gap-1 rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success">
            <Receipt className="size-3" />
            Turno activo
          </span>
        ) : null
      }
    />
  );
};

// ---------- Generic card ----------

const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  cta,
  to,
  badge,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  value: number | string;
  subtitle: string;
  cta: string;
  to: string;
  badge?: ReactNode;
}) => (
  <Card>
    <CardHeader className="flex-row items-center gap-3">
      <span className="flex size-10 items-center justify-center rounded-md bg-fg text-fg-inverse">
        <Icon className="size-5" />
      </span>
      <CardTitle className="flex-1">{title}</CardTitle>
      {badge}
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-black tracking-tight">{value}</div>
      <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>
      <Button
        asChild
        variant="outline"
        className="mt-4 w-full transition-all duration-200 hover:-translate-y-0.5 hover:border-fg hover:bg-fg hover:text-fg-inverse hover:shadow-lg"
      >
        <Link to={to}>{cta}</Link>
      </Button>
    </CardContent>
  </Card>
);
